// OnboardingWizard.js — the single post-registration setup flow.
//
// Replaces the old "jumps between menus" sequence (SchoolSetup → WelcomeModal →
// StudentIdSetupModal, each a separate surface) with ONE stepped wizard:
//   You → School → Tools → Sign-out → Finish
// It collects the teacher's title/school, which tools they want, and how they
// sign students out, then trims the seeded sample class to match (Michael:
// "no Library section if they turned Library off") and drops them into a live app.
//
// Writes only owner-self fields on the user doc (features / studentIdScheme /
// libraryEnabled / onboarding) plus an optional lunch flag on their own school —
// no security-rules change. The sample class itself is seeded at registration;
// here we prune it to the chosen tools on finish.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { firestore, auth } from "../shared/FirebaseConfig";
import { requestToJoinSchool } from "../shared/joinRequests";
import { pruneSampleToFeatures } from "../shared/sampleData";
import SchoolDropdown from "../teacher/SchoolDropdown";
import AddSchoolModal from "../teacher/AddSchoolModal";
import logo from "../../images/logo.svg";
import "./auth.css";
import "./OnboardingWizard.css";

const TITLES = ["Ms.", "Miss", "Mrs.", "Mr.", "Dr.", "Prof.", "Other"];
const WELCOME_PREFIX = "classboon_welcome_seen";

// The tools a teacher can turn on/off. Sign-Out / Attendance / Check List default
// on (the everyday core); Library / Lending default on too because the sample
// seeds them — turning one off prunes it from the sample.
const TOOLS = [
  { key: "signOut",    icon: "🚪", label: "Sign-Out",  blurb: "Students sign out to the bathroom, nurse, office…" },
  { key: "attendance", icon: "✅", label: "Attendance", blurb: "Take daily attendance from a class roster." },
  { key: "checklist",  icon: "📋", label: "Check List", blurb: "Per-class checklists + emergency/drill accountability." },
  { key: "library",    icon: "📚", label: "Library",    blurb: "Check books in and out of a classroom library." },
  { key: "lending",    icon: "🔧", label: "Lending",    blurb: "Track Chromebooks, calculators, or anything you lend." },
];

const ID_SCHEMES = [
  { key: "none",   icon: "🅰️", title: "No ID numbers",           blurb: "Students tap their name from a grid. Best for younger grades." },
  { key: "school", icon: "🏫", title: "School / district IDs",   blurb: "Each student has a school-wide ID, shared with other staff." },
  { key: "class",  icon: "🔢", title: "My own class numbers",    blurb: "You number your own students (like 1–24). Private to your class." },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const uid = auth.currentUser?.uid;

  const [stepKey, setStepKey] = useState("you");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // You
  const [title, setTitle] = useState(auth.currentUser?.displayName?.match(/^(Ms\.|Miss|Mrs\.|Mr\.|Dr\.|Prof\.)/)?.[0] || "Ms.");

  // School
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [requested, setRequested] = useState(false);

  // Tools + sub-questions
  const [features, setFeatures] = useState({
    signOut: true, attendance: true, checklist: true, library: true, lending: true,
  });
  const [lunch, setLunch] = useState(false);

  // Sign-out
  const [idScheme, setIdScheme] = useState(null);
  const [requireId, setRequireId] = useState(false);

  // The step sequence is dynamic: the Sign-out step only appears if Sign-Out is on.
  const steps = ["you", "school", "tools", ...(features.signOut ? ["signout"] : []), "finish"];
  const idx = Math.max(0, steps.indexOf(stepKey));

  const toggle = (key) => setFeatures((f) => ({ ...f, [key]: !f[key] }));

  const goNext = async () => {
    setError("");
    // Save title on leaving the "you" step so displayName is correct downstream.
    if (stepKey === "you" && uid) {
      try {
        const lastName = auth.currentUser.displayName?.split(" ").slice(-1)[0] || "";
        const displayName = title === "Other" ? auth.currentUser.displayName : `${title} ${lastName}`.trim();
        await updateProfile(auth.currentUser, { displayName });
        await updateDoc(doc(firestore, "users", uid), { title, displayName });
      } catch (e) { console.error("save title:", e); }
    }
    // Fire the join request (once) when leaving the school step with a selection.
    if (stepKey === "school" && selectedSchool?.id && !requested && uid) {
      try {
        await requestToJoinSchool({ uid, user: auth.currentUser, school: selectedSchool });
        setRequested(true);
      } catch (e) {
        setError("Couldn't send your join request — you can set your school later in Settings.");
      }
    }
    setStepKey(steps[Math.min(steps.length - 1, idx + 1)]);
  };

  const goBack = () => { setError(""); setStepKey(steps[Math.max(0, idx - 1)]); };

  // Persist every choice, trim the sample to the chosen tools, then route on.
  const finish = async (destination) => {
    if (!uid) { navigate("/dashboard"); return; }
    setSaving(true);
    try {
      const update = {
        features: {
          signOut: features.signOut, attendance: features.attendance, checklist: features.checklist,
          library: features.library, lending: features.lending,
        },
        libraryEnabled: features.library,
        onboarding: { complete: true, completedAt: new Date().toISOString() },
      };
      if (features.signOut && idScheme) {
        update.studentIdScheme = idScheme;
        update.nameSignOut = idScheme === "none";
        update.signInRequiresId = idScheme !== "none" && requireId;
      }
      await updateDoc(doc(firestore, "users", uid), update);

      // Optional lunch counts → their own school doc (they own their provisional
      // school; harmless if they've requested to join a real one — admin controls
      // it there). Best-effort.
      if (features.attendance && lunch) {
        try {
          const userSnap = await getDoc(doc(firestore, "users", uid));
          const sid = userSnap.get("schoolId");
          if (sid) await updateDoc(doc(firestore, "schools", sid), { lunchEnabled: true });
        } catch (e) { console.error("set lunch:", e); }
      }

      // Trim the seeded sample to match the tools they kept.
      try { await pruneSampleToFeatures(uid, features); } catch (e) { console.error("prune sample:", e); }

      // The wizard replaces the standalone WelcomeModal — suppress it so they
      // aren't greeted twice.
      try { localStorage.setItem(`${WELCOME_PREFIX}_${uid}`, "true"); } catch { /* private mode */ }
    } catch (e) {
      console.error("finish onboarding:", e);
    } finally {
      setSaving(false);
    }
    if (destination === "import") navigate("/importclasses");
    else navigate("/dashboard", { state: destination === "tour" ? { startTour: true } : undefined });
  };

  const first = (auth.currentUser?.displayName || "").replace(/^(Ms\.|Miss|Mrs\.|Mr\.|Dr\.|Prof\.)\s*/, "").split(" ")[0];

  return (
    <div className="auth-card ob-card">
      <div className="auth-head">
        <img src={logo} className="auth-logo" alt="classBOON" />
        <Progress steps={steps} current={idx} />
      </div>

      {stepKey === "you" && (
        <div className="ob-step">
          <h1 className="auth-title">Welcome{first ? `, ${first}` : ""}! 👋</h1>
          <p className="auth-sub">Let's set up your classroom — this takes about a minute.</p>
          <div className="auth-field">
            <label htmlFor="ob-title">Your title</label>
            <select id="ob-title" value={title} onChange={(e) => setTitle(e.target.value)}>
              {TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      )}

      {stepKey === "school" && (
        <div className="ob-step">
          <h1 className="auth-title">Your school</h1>
          <p className="auth-sub">
            Join your school to share a roster and emergency accountability with staff — or
            skip and use ClassBoon on your own. You can do this anytime in Settings.
          </p>
          <div className="auth-field">
            <label>Find your school</label>
            <SchoolDropdown onAddSchool={() => setShowAddSchool(true)} onSelectSchool={setSelectedSchool} />
          </div>
          {selectedSchool && (
            <div className="ob-school-chip">
              <strong>{selectedSchool.schoolName}</strong>
              <div>{selectedSchool.address}, {selectedSchool.city}, {selectedSchool.state} {selectedSchool.zipcode}</div>
              <p className="ob-note">We'll send a request to a school admin — until they approve it you're in your own space.</p>
            </div>
          )}
        </div>
      )}

      {stepKey === "tools" && (
        <div className="ob-step">
          <h1 className="auth-title">Which tools do you want?</h1>
          <p className="auth-sub">Turn off anything you won't use — it just hides it. You can flip these back on later in Settings.</p>
          <div className="ob-tools">
            {TOOLS.map((t) => (
              <div key={t.key} className="ob-tool">
                <label className="ob-tool-main">
                  <span className="ob-tool-icon" aria-hidden="true">{t.icon}</span>
                  <span className="ob-tool-text">
                    <span className="ob-tool-label">{t.label}</span>
                    <span className="ob-tool-blurb">{t.blurb}</span>
                  </span>
                  <input type="checkbox" className="ob-switch" checked={features[t.key]} onChange={() => toggle(t.key)} />
                </label>
                {t.key === "attendance" && features.attendance && (
                  <label className="ob-subtoggle">
                    <input type="checkbox" checked={lunch} onChange={(e) => setLunch(e.target.checked)} />
                    <span>Also track lunch counts at check-in</span>
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {stepKey === "signout" && (
        <div className="ob-step">
          <h1 className="auth-title">How will you identify students?</h1>
          <p className="auth-sub">This sets up your sign-out screen. Change it anytime in Settings → Sign-Out.</p>
          <div className="ob-options">
            {ID_SCHEMES.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`ob-option${idScheme === o.key ? " selected" : ""}`}
                onClick={() => { setIdScheme(o.key); if (o.key === "none") setRequireId(false); }}
              >
                <span className="ob-option-icon">{o.icon}</span>
                <span className="ob-option-text">
                  <span className="ob-option-title">{o.title}</span>
                  <span className="ob-option-blurb">{o.blurb}</span>
                </span>
              </button>
            ))}
          </div>
          {idScheme && idScheme !== "none" && (
            <div className="ob-followup">
              <p className="ob-followup-q">Require students to type their ID to sign back in?</p>
              <div className="ob-followup-actions">
                <button type="button" className={`ob-pill${requireId ? " selected" : ""}`} onClick={() => setRequireId(true)}>Yes — confirm it's them</button>
                <button type="button" className={`ob-pill${!requireId ? " selected" : ""}`} onClick={() => setRequireId(false)}>No — one-tap sign in</button>
              </div>
            </div>
          )}
        </div>
      )}

      {stepKey === "finish" && (
        <div className="ob-step">
          <h1 className="auth-title">You're all set! 🎉</h1>
          <p className="auth-sub">
            We've set up a <strong>sample class</strong> — with students, sign-out history and
            attendance — so you can try everything right now. Remove it in one click when your
            real roster is ready.
          </p>
          <div className="ob-finish-actions">
            <button type="button" className="ob-finish-primary" disabled={saving} onClick={() => finish("tour")}>
              Take a quick tour
            </button>
            <button type="button" className="ob-finish-secondary" disabled={saving} onClick={() => finish("explore")}>
              Explore the sample class
            </button>
            <button type="button" className="ob-finish-link" disabled={saving} onClick={() => finish("import")}>
              Skip — set up my real class now
            </button>
          </div>
        </div>
      )}

      {error && <p className="auth-error">{error}</p>}

      {stepKey !== "finish" && (
        <div className="ob-nav">
          {idx > 0
            ? <button type="button" className="ob-back" onClick={goBack} disabled={saving}>← Back</button>
            : <span />}
          <div className="ob-nav-right">
            {(stepKey === "school") && (
              <button type="button" className="ob-skip" onClick={goNext} disabled={saving}>Skip for now</button>
            )}
            <button type="button" className="ob-next" onClick={goNext} disabled={saving || (stepKey === "signout" && !idScheme)}>
              {saving ? "Saving…" : "Next →"}
            </button>
          </div>
        </div>
      )}

      {showAddSchool && (
        <AddSchoolModal
          onClose={() => setShowAddSchool(false)}
          onSchoolAdded={(newSchool) => { setSelectedSchool(newSchool); setShowAddSchool(false); }}
        />
      )}
    </div>
  );
}

function Progress({ steps, current }) {
  const LABELS = { you: "You", school: "School", tools: "Tools", signout: "Sign-out", finish: "Done" };
  return (
    <div className="ob-progress" aria-label={`Step ${current + 1} of ${steps.length}`}>
      {steps.map((s, i) => (
        <div key={s} className={`ob-dot${i === current ? " active" : ""}${i < current ? " done" : ""}`}>
          <span className="ob-dot-mark">{i < current ? "✓" : i + 1}</span>
          <span className="ob-dot-label">{LABELS[s]}</span>
        </div>
      ))}
    </div>
  );
}
